import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useTripsStore } from '@/store/trips';
import dayjs from 'dayjs';

export function useRedemptionForm() {
    const tripsStore = useTripsStore();
    const {
        activeRedemption,
        activeTrip,
        trip,
        classOptions,
        redemptionTypes,
        showRedemptionCreateModal,
        tripFormErrors,
    } = storeToRefs(tripsStore);

    const redemptionType = computed(() => {
        return redemptionTypes.value.find(
            (rt) => rt.id === activeRedemption.value.redemption_type_id
        );
    });

    const classOfService = computed({
        get: () => {
            if (typeof activeRedemption.value.class_of_service === 'string') {
                return (
                    classOptions.value.find(
                        (option) => option.value === activeRedemption.value.class_of_service
                    ) || null
                );
            }
            return activeRedemption.value.class_of_service;
        },
        set: (value) => {
            activeRedemption.value.class_of_service = value?.value || null;
        },
    });

    const segments = computed(() => {
        return activeRedemption.value.travel_segments &&
            activeRedemption.value.travel_segments.length
            ? activeRedemption.value.travel_segments
            : [{ origin: null, destination: null, travel_number: null }];
    });

    // Date state
    const differentArrivalDate = ref(false);

    const getDefaultStartDate = () => {
        const tripData = trip.value || activeTrip.value;
        return tripData?.start_date || new Date();
    };

    const dateStartConfig = ref({
        dateFormat: 'm/d/Y',
        defaultDate: getDefaultStartDate(),
    });

    const dateEndConfig = ref({
        dateFormat: 'm/d/Y',
    });

    const updateDefaultDates = () => {
        const tripData = trip.value || activeTrip.value;
        if (tripData?.start_date) {
            const defaultDate = new Date(tripData.start_date);
            dateStartConfig.value.defaultDate = defaultDate;
            if (!activeRedemption.value.start_date) {
                activeRedemption.value.start_date = dayjs(defaultDate).format('YYYY-MM-DD');
            }
        }
    };

    const onStartChange = (selectedDates, dateStr, instance) => {
        dateEndConfig.value.minDate = dateStr;
        if (redemptionType.value?.slug === 'flight' && differentArrivalDate.value && selectedDates.length > 0) {
            const startDate = selectedDates[0];
            const endDate = dayjs(startDate).add(1, 'day');
            activeRedemption.value.end_date = endDate.format('MM/DD/YYYY');
        }
    };

    const onEndChange = (selectedDates, dateStr, instance) => {
        dateStartConfig.value.maxDate = dateStr;
    };

    const onDepartureDateOpen = () => {
        if ((redemptionType.value?.slug === 'flight' || redemptionType.value?.slug === 'train') && differentArrivalDate.value) {
            activeRedemption.value.end_date = null;
            dateEndConfig.value.minDate = null;
            dateStartConfig.value.maxDate = null;
        }
    };

    const handleSegmentsUpdated = (segments) => {
        tripsStore.setSegments(segments);
    };

    // Watchers
    watch(showRedemptionCreateModal, (val) => {
        if (val) {
            differentArrivalDate.value = !!activeRedemption.value.end_date;
            updateDefaultDates();
        }
    });

    watch([trip, activeTrip], () => {
        updateDefaultDates();
    }, { deep: true });

    watch(differentArrivalDate, (val) => {
        if (!val) {
            activeRedemption.value.end_date = null;
        }
    });

    return {
        // Store refs
        activeRedemption, activeTrip, trip, classOptions,
        redemptionTypes, tripFormErrors, tripsStore,
        // Computed
        redemptionType, classOfService, segments,
        // Date state & handlers
        dateStartConfig, dateEndConfig, differentArrivalDate,
        onStartChange, onEndChange, onDepartureDateOpen,
        updateDefaultDates,
        // Segments
        handleSegmentsUpdated,
    };
}
